import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { articlesService } from '../services/articles'
import { subcontractorsService } from '../services/subcontractors'
import { priceListsService } from '../services/priceLists'
import { unavailabilityService, type UnavailabilityInsert } from '../services/unavailability'
import { rutApplicantsService, type RutApplicantInsert, type RutApplicantUpdate } from '../services/rutApplicants'
import { supabase, type Database } from '../lib/supabase'


type ArticleUpdate = Database['public']['Tables']['articles']['Update']
type SubcontractorUpdate = Database['public']['Tables']['subcontractors']['Update']
type PriceListUpdate = Database['public']['Tables']['price_lists']['Update']
type ArticleCategoryInsert = Database['public']['Tables']['article_categories']['Insert']
type ArticleCategoryUpdate = Database['public']['Tables']['article_categories']['Update']

// React Query keys
export const queryKeys = {
  articles: ['articles'] as const,
  article: (id: string) => ['articles', id] as const,
  articleCategories: ['articleCategories'] as const,
  articleCategory: (id: string) => ['articleCategories', id] as const,
  customers: ['customers'] as const,
  customer: (id: string) => ['customers', id] as const,
  customerRutApplicants: (id: string) => ['customers', id, 'rutApplicants'] as const,
  subcontractors: ['subcontractors'] as const,
  subcontractor: (id: string) => ['subcontractors', id] as const,
  subcontractorUnavailability: (id: string) => ['subcontractors', id, 'unavailability'] as const,
  unavailability: ['unavailability'] as const,
  assignments: ['assignments'] as const,
  assignment: (id: string) => ['assignments', id] as const,
  priceLists: ['priceLists'] as const,
  priceList: (id: string) => ['priceLists', id] as const,
  priceListArticles: (id: string) => ['priceLists', id, 'articles'] as const,
}

// Hook för artiklar
export const useArticles = () => {
  return useQuery({
    queryKey: queryKeys.articles,
    queryFn: articlesService.getAll,
    staleTime: 5 * 60 * 1000, // 5 minuter
  })
}

// Hook för artiklar med priser från standardprislistor
export const useArticlesWithDefaultPrices = () => {
  return useQuery({
    queryKey: [...queryKeys.articles, 'withDefaultPrices'],
    queryFn: async () => {
      // Hämta standardprislistor
      const { data: defaultPriceLists, error: plError } = await supabase
        .from('price_lists')
        .select('id, type')
        .eq('is_default', true)
        .eq('is_active', true)

      if (plError) throw plError

      const customerPriceListId = defaultPriceLists?.find(pl => pl.type === 'customer')?.id
      const subcontractorPriceListId = defaultPriceLists?.find(pl => pl.type === 'subcontractor')?.id

      // Hämta artiklar
      const { data: articles, error: artError } = await supabase
        .from('articles')
        .select('*')
        .order('name')

      if (artError) throw artError

      // Hämta priser från kundprislista
      let customerPrices: Record<string, number> = {}
      if (customerPriceListId) {
        const { data: prices } = await supabase
          .from('price_list_articles')
          .select('article_id, price_excl_vat')
          .eq('price_list_id', customerPriceListId)

        if (prices) {
          customerPrices = Object.fromEntries(prices.map(p => [p.article_id, p.price_excl_vat]))
        }
      }

      // Hämta priser från trädgårdsmästarprislista
      let subcontractorPrices: Record<string, number> = {}
      if (subcontractorPriceListId) {
        const { data: prices } = await supabase
          .from('price_list_articles')
          .select('article_id, price_excl_vat')
          .eq('price_list_id', subcontractorPriceListId)

        if (prices) {
          subcontractorPrices = Object.fromEntries(prices.map(p => [p.article_id, p.price_excl_vat]))
        }
      }

      // Kombinera data
      return articles?.map(article => ({
        ...article,
        customerPrice: customerPrices[article.id] || null,
        subcontractorPrice: subcontractorPrices[article.id] || null
      })) || []
    },
    staleTime: 5 * 60 * 1000,
  })
}

export const useArticle = (id: string) => {
  return useQuery({
    queryKey: queryKeys.article(id),
    queryFn: () => articlesService.getById(id),
    enabled: !!id,
  })
}

export const useCreateArticle = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: articlesService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.articles })
    },
  })
}

export const useUpdateArticle = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: ArticleUpdate }) =>
      articlesService.update(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.articles })
      queryClient.invalidateQueries({ queryKey: queryKeys.article(data.id) })
    },
  })
}

export const useDeleteArticle = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: articlesService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.articles })
    },
  })
}

// Hook för RUT-artiklar
export const useRutArticles = () => {
  return useQuery({
    queryKey: [...queryKeys.articles, 'rut'],
    queryFn: articlesService.getRutApplicable,
    staleTime: 5 * 60 * 1000,
  })
}

// Hook för artiklar efter kategori
export const useArticlesByCategory = (category: string) => {
  return useQuery({
    queryKey: [...queryKeys.articles, 'category', category],
    queryFn: () => articlesService.getByCategory(category),
    enabled: !!category,
    staleTime: 5 * 60 * 1000,
  })
}

// Utility hook för att kombinera data
export const useArticlesData = () => {
  const { data: articles, isLoading, error } = useArticles()
  const { data: rutArticles } = useRutArticles()
  
  return {
    articles: articles || [],
    rutArticles: rutArticles || [],
    isLoading,
    error,
    // Beräkna statistik
    stats: {
      total: articles?.length || 0,
      rutApplicable: rutArticles?.length || 0,
      nonRut: (articles?.length || 0) - (rutArticles?.length || 0),
    }
  }
}

// Hook för trädgårdsmästare
export const useSubcontractors = () => {
  return useQuery({
    queryKey: queryKeys.subcontractors,
    queryFn: subcontractorsService.getAll,
    staleTime: 5 * 60 * 1000, // 5 minuter
  })
}

export const useSubcontractor = (id: string) => {
  return useQuery({
    queryKey: queryKeys.subcontractor(id),
    queryFn: () => subcontractorsService.getById(id),
    enabled: !!id,
  })
}

export const useCreateSubcontractor = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: subcontractorsService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subcontractors })
    },
  })
}

export const useUpdateSubcontractor = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: SubcontractorUpdate }) =>
      subcontractorsService.update(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subcontractors })
      queryClient.invalidateQueries({ queryKey: queryKeys.subcontractor(data.id) })
    },
  })
}

export const useDeleteSubcontractor = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: subcontractorsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subcontractors })
    },
  })
}

// ============================================
// Hooks för trädgårdsmästares frånvaro
// ============================================

export const useSubcontractorUnavailability = (subcontractorId: string) => {
  return useQuery({
    queryKey: queryKeys.subcontractorUnavailability(subcontractorId),
    queryFn: () => unavailabilityService.getBySubcontractor(subcontractorId),
    enabled: !!subcontractorId,
    staleTime: 5 * 60 * 1000, // 5 minuter
  })
}

export const useUnavailabilityBySubcontractorIds = (ids: string[]) => {
  return useQuery({
    queryKey: [...queryKeys.unavailability, 'byIds', ids.sort().join(',')],
    queryFn: () => unavailabilityService.getBySubcontractorIds(ids),
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000,
  })
}

export const useCreateUnavailability = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UnavailabilityInsert) => unavailabilityService.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subcontractorUnavailability(data.subcontractor_id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.unavailability })
    },
  })
}

export const useDeleteUnavailability = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; subcontractorId: string }) =>
      unavailabilityService.delete(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subcontractorUnavailability(variables.subcontractorId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.unavailability })
    },
  })
}

// ============================================
// Hooks för kunders RUT-sökande
// ============================================

export const useCustomerRutApplicants = (customerId: string) => {
  return useQuery({
    queryKey: queryKeys.customerRutApplicants(customerId),
    queryFn: () => rutApplicantsService.getByCustomer(customerId),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  })
}

export const useCreateRutApplicant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RutApplicantInsert) => rutApplicantsService.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerRutApplicants(data.customer_id) })
    },
  })
}

export const useUpdateRutApplicant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; customerId: string; updates: RutApplicantUpdate }) =>
      rutApplicantsService.update(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerRutApplicants(variables.customerId) })
    },
  })
}

export const useDeleteRutApplicant = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; customerId: string }) =>
      rutApplicantsService.delete(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerRutApplicants(variables.customerId) })
    },
  })
}

export const useMoveRutApplicantUp = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ customerId, applicantId }: { customerId: string; applicantId: string }) =>
      rutApplicantsService.moveUp(customerId, applicantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerRutApplicants(variables.customerId) })
    },
  })
}

export const useMoveRutApplicantDown = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ customerId, applicantId }: { customerId: string; applicantId: string }) =>
      rutApplicantsService.moveDown(customerId, applicantId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customerRutApplicants(variables.customerId) })
    },
  })
}

// ============================================
// Hook för prislistor
// ============================================

export const usePriceLists = () => {
  return useQuery({
    queryKey: queryKeys.priceLists,
    queryFn: priceListsService.getAll,
    staleTime: 5 * 60 * 1000,
  })
}

export const usePriceListsByType = (type: 'customer' | 'subcontractor') => {
  return useQuery({
    queryKey: [...queryKeys.priceLists, 'type', type],
    queryFn: () => priceListsService.getByType(type),
    staleTime: 5 * 60 * 1000,
  })
}

export const usePriceList = (id: string) => {
  return useQuery({
    queryKey: queryKeys.priceList(id),
    queryFn: () => priceListsService.getById(id),
    enabled: !!id && id !== 'new',
  })
}

export const usePriceListArticles = (priceListId: string) => {
  return useQuery({
    queryKey: queryKeys.priceListArticles(priceListId),
    queryFn: () => priceListsService.getArticles(priceListId),
    enabled: !!priceListId && priceListId !== 'new',
  })
}

export const useCreatePriceList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: priceListsService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
    },
  })
}

export const useUpdatePriceList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: PriceListUpdate }) =>
      priceListsService.update(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
      queryClient.invalidateQueries({ queryKey: queryKeys.priceList(data.id) })
    },
  })
}

export const useDeletePriceList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: priceListsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
    },
  })
}

export const useCopyPriceList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, newName }: { id: string; newName: string }) =>
      priceListsService.copy(id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
    },
  })
}

export const useCopyPricesFrom = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ fromPriceListId, toPriceListId }: { fromPriceListId: string; toPriceListId: string }) =>
      priceListsService.copyPricesFrom(fromPriceListId, toPriceListId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceListArticles(variables.toPriceListId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
    },
  })
}

export const useCanDeletePriceList = (id: string) => {
  return useQuery({
    queryKey: [...queryKeys.priceList(id), 'canDelete'],
    queryFn: () => priceListsService.canDelete(id),
    enabled: !!id && id !== 'new',
  })
}

export const useSetPriceListArticlePrices = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ priceListId, prices }: {
      priceListId: string
      prices: { articleId: string; priceExclVat: number }[]
    }) => priceListsService.setArticlePrices(priceListId, prices),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.priceListArticles(variables.priceListId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
    },
  })
}

// Hook för att hämta alla priser för en artikel i alla prislistor
export const useArticlePricesAcrossLists = (articleId: string) => {
  return useQuery({
    queryKey: ['articlePrices', articleId],
    queryFn: () => priceListsService.getArticlePricesAcrossLists(articleId),
    enabled: !!articleId && articleId !== 'new',
  })
}

// Hook för att sätta pris för en artikel i en prislista
export const useSetArticlePriceInList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ priceListId, articleId, priceExclVat }: {
      priceListId: string
      articleId: string
      priceExclVat: number | null
    }) => priceListsService.setArticlePriceInList(priceListId, articleId, priceExclVat),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['articlePrices', variables.articleId] })
      queryClient.invalidateQueries({ queryKey: queryKeys.priceListArticles(variables.priceListId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.priceLists })
    },
  })
}

// ============================================
// Hooks för artikelkategorier
// ============================================

export const useArticleCategories = () => {
  return useQuery({
    queryKey: queryKeys.articleCategories,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('article_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })

      if (error) throw error
      return data
    },
    staleTime: 5 * 60 * 1000, // 5 minuter
  })
}

export const useAllArticleCategories = () => {
  return useQuery({
    queryKey: [...queryKeys.articleCategories, 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('article_categories')
        .select('*')
        .order('sort_order', { ascending: true })

      if (error) throw error
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export const useArticleCategory = (id: string) => {
  return useQuery({
    queryKey: queryKeys.articleCategory(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('article_categories')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!id && id !== 'new',
  })
}

export const useCreateArticleCategory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (category: ArticleCategoryInsert) => {
      const { data, error } = await supabase
        .from('article_categories')
        .insert(category)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.articleCategories })
    },
  })
}

export const useUpdateArticleCategory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: ArticleCategoryUpdate }) => {
      const { data, error } = await supabase
        .from('article_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.articleCategories })
      queryClient.invalidateQueries({ queryKey: queryKeys.articleCategory(data.id) })
    },
  })
}

export const useDeleteArticleCategory = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('article_categories')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.articleCategories })
    },
  })
}

